import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetBuyerInventoryLocations,
  useGetProductCategories,
  useGetProductFilterOptions,
} from '~/api/product';
import { Input, Select } from '~/components/ui';
import { useDebounce } from '~/hooks/useDebounce';
import { useGetPropertyValues } from '~/api/product-properties';
import type {
  PlatformProductCategory,
  ProductPropertyKey,
} from '~/graphql/graphql';
import { MultiSelect } from '~/components/ui/inputs/MultiSelect';

export interface ProductFilters {
  /** Category drives the property filters and dimensions */
  category?: PlatformProductCategory;
  dimension: string;
  stockLevel: string;
  location: string;
  searchTerm: string;
  /** Dynamic properties; values depend on category */
  properties: Record<string, string | string[]>;
}

interface ProductFilterProps {
  /** Emit the entire filter state upward */
  onChange: (filters: ProductFilters) => void;
}

export function ProductFilter({ onChange }: ProductFilterProps) {
  const { t } = useTranslation('product');

  /** Local UI state */
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProductFilters>({
    category: undefined,
    dimension: '',
    stockLevel: '',
    location: '',
    searchTerm: '',
    properties: {},
  });

  const { category } = filters;

  const { data: categoriesData } = useGetProductCategories();
  const { data: locationData } = useGetBuyerInventoryLocations();
  const locationOptions =
    locationData?.allUniqueProductInventoryLocationsForBuyer.map((l) => {
      const { addressLine1, city, region, zipCode } = l.facility.address;
      const cityStateZip = [city, region, zipCode].filter(Boolean).join(', ');
      const address = [addressLine1, cityStateZip].filter(Boolean).join(', ');
      const label = address ? `${l.name} - ${address}` : l.name;
      return { label, value: l.id };
    }) ?? [];
  locationOptions.unshift({
    label: t('product.filter.all_locations'),
    value: '',
  });

  const { data: productFilters, isLoading: isMetaLoading } =
    useGetProductFilterOptions(category);

  const dimensionsOptions = useMemo(() => {
    const base =
      productFilters?.platformProductDimensions?.map((d) => ({
        label: `${d.nominalThicknessInches}X${d.nominalWidthInches}`,
        value: `${d.nominalThicknessInches}X${d.nominalWidthInches}`,
      })) ?? [];
    return [{ label: t('product.filter.all_dimensions'), value: '' }, ...base];
  }, [productFilters?.platformProductDimensions, t]);

  function applyFilters(updated: ProductFilters) {
    setFilters(updated);
    onChange(updated);
  }

  function changeCategory(next: PlatformProductCategory | undefined) {
    if (next === filters.category) {
      return;
    }
    applyFilters({
      ...filters,
      category: next,
      properties: {},
      ...(next ? {} : { dimension: '' }),
    });
  }

  function handleChange<K extends keyof ProductFilters>(
    key: K,
    value: ProductFilters[K]
  ) {
    applyFilters({ ...filters, [key]: value });
  }

  const debouncedSearchChange = useDebounce(handleChange, 400);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearchChange('searchTerm', value);
  };

  const stockLevelOptions = [
    { label: t('product.filter.all_stock_levels'), value: '' },
    { label: t('product.filter.stock_low'), value: 'low' },
    { label: t('product.filter.stock_medium'), value: 'medium' },
    { label: t('product.filter.stock_high'), value: 'high' },
  ];

  /** Category-aware dynamic property select */
  function DynamicPropertySelect({
    propertyKey,
    propertyDisplay,
    isArray,
    value,
    onChange,
  }: {
    propertyKey: string;
    propertyDisplay: string;
    isArray: boolean;
    value: string | string[] | undefined;
    onChange: (value: string | string[]) => void;
  }) {
    const { data, isLoading } = useGetPropertyValues(category, propertyKey);

    const baseOptions =
      data?.productPropertyValues.map((v) => ({
        label: v.displayValue,
        value: v.value,
      })) ?? [];

    const options = isArray
      ? baseOptions
      : [{ label: `All ${propertyDisplay}`, value: '' }, ...baseOptions];

    if (isArray) {
      return (
        <MultiSelect
          placeholder={`All ${propertyDisplay}`}
          name={propertyKey}
          disabled={isLoading}
          value={(value as string[]) ?? []}
          options={baseOptions}
          onChange={(_, values) => onChange(values)}
        />
      );
    }

    return (
      <Select
        name={propertyKey}
        disabled={isLoading}
        value={(value as string) ?? ''}
        options={options}
        onChange={(_, v) => onChange(v)}
      />
    );
  }

  const showMetaLoading = !!category && isMetaLoading;

  return (
    <div className="bg-neut-bg-alt rounded-lg border border-neut-border p-4 sm:p-6 shadow-sm">
      <div className="grid grid-cols-1 gap-4">
        <div className="relative">
          <Input
            name="searchTerm"
            type="text"
            placeholder={t('product.filter.search_placeholder')}
            value={searchTerm}
            onChange={(_, value) => handleSearchChange(value)}
            leftIcon={
              <Search size={18} className="text-neut-400" aria-hidden="true" />
            }
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            name="category"
            value={category ?? ''}
            options={[
              { label: t('product.filter.all_categories'), value: '' },
              ...(categoriesData?.platformProductCategories ?? []),
            ]}
            onChange={(_, v) =>
              changeCategory(
                (v || undefined) as PlatformProductCategory | undefined
              )
            }
          />

          <Select
            name="dimension"
            value={filters.dimension}
            options={dimensionsOptions}
            onChange={(_, value) => handleChange('dimension', value)}
          />

          <Select
            name="stockLevel"
            value={filters.stockLevel}
            options={stockLevelOptions}
            onChange={(_, value) => handleChange('stockLevel', value)}
          />
          <Select
            name="location"
            value={filters.location}
            options={locationOptions}
            onChange={(_, value) => handleChange('location', value)}
          />
        </div>
        {!category ? null : (
          <>
            {showMetaLoading ? (
              <p className="w-full text-center">
                {t('product.filter.loading')}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {productFilters?.productPropertyKeys?.map(
                  (prop: ProductPropertyKey) => (
                    <DynamicPropertySelect
                      key={prop.key}
                      propertyKey={prop.key}
                      propertyDisplay={prop.displayName}
                      isArray={prop.isArray}
                      value={filters.properties[prop.key]}
                      onChange={(value) =>
                        handleChange('properties', {
                          ...filters.properties,
                          [prop.key]: value,
                        })
                      }
                    />
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
